import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from '@mui/material';
import { Container as DraggableContainer, Draggable } from 'react-smooth-dnd';

import { ReactComponent as TrashIcon } from '../../../../../assets/icons/v2/trash.svg';
import { ReactComponent as DragIcon } from '../../../../../assets/icons/v2/drag.svg';
import { ReactComponent as AddCircleIcon } from '../../../../../assets/icons/v2/add.svg';

import { PrimaryButton } from '../../../../../components/Button/ActionButtons';
import { StyledTextField } from '../../../../../components/Inputs';

import {
  SectionLabel,
  TitleTextField,
  FeatureRow,
  DragHandle,
  FeatureInputs,
} from './styles';
import texts from './texts.json';

const FEATURE_FIELDS = {
  TITLE: 'title',
  DESCRIPTION: 'description',
};

const FeatureList = ({ features, onChange, isReadOnly }) => {
  const handleDrop = useCallback((dropResult) => {
    const { removedIndex, addedIndex } = dropResult;
    if (removedIndex === null && addedIndex === null) return;

    const updated = [...features];
    const [removed] = updated.splice(removedIndex, 1);
    updated.splice(addedIndex, 0, removed);
    onChange(updated);
  }, [features, onChange]);

  const handleFeatureChange = useCallback((index, field, value) => {
    onChange(features.map((f, i) => (i === index ? { ...f, [field]: value } : f)));
  }, [features, onChange]);

  const handleDeleteFeature = useCallback((index) => {
    onChange(features.filter((_, i) => i !== index));
  }, [features, onChange]);

  const handleAddFeature = useCallback(() => {
    onChange([...features, { id: Date.now(), title: '', description: '' }]);
  }, [features, onChange]);

  return (
    <div>
      <SectionLabel>{texts.sectionTitle}</SectionLabel>
      <DraggableContainer
        dragHandleSelector=".drag-handle"
        lockAxis="y"
        onDrop={handleDrop}
      >
        {features.map((feature, index) => (
          <Draggable key={feature.id ?? index}>
            <FeatureRow>
              {!isReadOnly && (
                <DragHandle className="drag-handle">
                  <DragIcon />
                </DragHandle>
              )}
              <FeatureInputs>
                <TitleTextField
                  value={feature.title}
                  onChange={(e) => handleFeatureChange(index, FEATURE_FIELDS.TITLE, e.target.value)}
                  placeholder={texts.titlePlaceholder}
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={isReadOnly}
                />
                <StyledTextField
                  value={feature.description}
                  onChange={(e) => handleFeatureChange(index, FEATURE_FIELDS.DESCRIPTION, e.target.value)}
                  placeholder={texts.descriptionPlaceholder}
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  disabled={isReadOnly}
                />
              </FeatureInputs>
              {!isReadOnly && (
                <Tooltip title={texts.removeTooltip}>
                  <PrimaryButton
                    variant="info"
                    size="small"
                    $fitToContent
                    icon={<TrashIcon />}
                    onClick={() => handleDeleteFeature(index)}
                  />
                </Tooltip>
              )}
            </FeatureRow>
          </Draggable>
        ))}
      </DraggableContainer>
      {!isReadOnly && (
        <PrimaryButton
          variant="light"
          size="small"
          $fitToContent
          icon={<AddCircleIcon />}
          onClick={handleAddFeature}
        >
          {texts.addButton}
        </PrimaryButton>
      )}
    </div>
  );
};

FeatureList.propTypes = {
  features: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    description: PropTypes.string,
  })).isRequired,
  onChange: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
};

FeatureList.defaultProps = {
  isReadOnly: false,
};

export default FeatureList;
